/**
 * SMBSignupModal component
 *
 * Modal wrapper for SMB signup flow
 * Non-dismissable for unlogged users
 * Single-column layout with expert context bar
 */
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { SignInHandoff } from '@/auth/utils/signInHandoff';
import Picture from '@/components/Picture';
import { surfaceElevated } from '@/theme/colors';

import SMBLoginForm from './SMBLoginForm';
import SMBSignupForm from './SMBSignupForm';
import { SMBSignupModalProps } from './types';
import { trackPhase1ModalClosed, trackPhase1ModalOpened } from './utils/analytics';
import { formatExpertNameWithInitial, getFirstName } from './utils/formatExpertName';

type View = 'signup' | 'login';

const useStyles = makeStyles(() => ({
  dialogContent: {
    padding: 0, // No padding - expert bar will meet edges
  },
}));

/* eslint-disable react/prop-types */
export const SMBSignupModal: React.FC<SMBSignupModalProps> = ({
  open,
  onClose,
  expert,
  returnTo,
}) => {
  const s = useStyles();
  const [view, setView] = useState<View>('signup');
  const [signInHandoff, setSignInHandoff] = useState<SignInHandoff | undefined>(undefined);
  const modalOpenTime = useRef<Date | null>(null);
  const viewRef = useRef(view);
  const expertRef = useRef(expert);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  useEffect(() => {
    expertRef.current = expert;
  }, [expert]);

  // Track modal opened on mount
  useEffect(() => {
    if (open) {
      modalOpenTime.current = new Date();
      trackPhase1ModalOpened(expert);
    }
  }, [open, expert]);

  // Track modal closed only on unmount
  useEffect(() => {
    return () => {
      if (modalOpenTime.current) {
        trackPhase1ModalClosed(viewRef.current, modalOpenTime.current, expertRef.current);
      }
    };
  }, []);

  const handleToggleView = useCallback(() => {
    setSignInHandoff(undefined);
    setView((prev) => (prev === 'signup' ? 'login' : 'signup'));
  }, []);

  // The account was created but not signed in: resubmitting signup can only
  // hit "already in use", so switch to login with the email filled in.
  const handleSignInRequired = useCallback((handoff: SignInHandoff) => {
    setSignInHandoff(handoff);
    setView('login');
  }, []);

  const handleSuccess = useCallback(() => {
    // User is now logged in, close the modal
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleError = useCallback((error: Error) => {
    console.error('SMB signup error:', error);
  }, []);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm" // Smaller modal for single column
      disableEscapeKeyDown // Prevent ESC key from closing
      onClose={(_event, reason) => {
        // Prevent closing via backdrop click
        if (reason === 'backdropClick') {
          return;
        }
        // Still allow programmatic close
        if (onClose) {
          onClose();
        }
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)', // Dark semi-transparent backdrop
            backdropFilter: 'blur(10px)', // Blur the background
          },
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: surfaceElevated,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)', // Add shadow for depth
        },
      }}
    >
      <DialogContent className={s.dialogContent}>
        {/* Expert Context Bar - Meets top borders with side padding */}
        {expert && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1.5,
              pl: 3, // 24px left padding (matches content area)
              pr: 4, // 32px right padding
              bgcolor: 'var(--surfaceElevated)',
              border: '1px solid',
              borderColor: 'var(--surfaceBorder)',
              borderLeft: 'none',
              borderRight: 'none',
              borderTop: 'none',
            }}
          >
            <Picture
              user={{
                name: expert.name,
                picture_url: expert.pictureUrl,
              }}
              size={44}
              link={false}
              ring={false}
            />
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: '2px' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: 12,
                  }}
                >
                  Viewing
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    fontWeight: 700,
                    color: 'text.primary',
                  }}
                >
                  {formatExpertNameWithInitial(expert.name)}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  color: 'text.secondary',
                  fontSize: 12,
                  mt: '1px',
                }}
              >
                {expert.title}
              </Typography>
            </Box>
            <LockOutlinedIcon sx={{ fontSize: 20, ml: 1.5 }} />
          </Box>
        )}

        {/* Content area with padding */}
        <Box sx={{ p: 3 }}>
          {/* V2 Copy - Left aligned */}
          <Box sx={{ mb: view === 'login' ? 0.5 : 2.75, pl: '2px', pr: '7px' }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1.5, fontSize: 24 }}>
              {view === 'login'
                ? 'Welcome back'
                : expert
                  ? "Unlock this expert's full profile"
                  : 'Get free access'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {view === 'login'
                ? expert
                  ? `Log in to view ${getFirstName(expert.name)}'s full profile and connect with experts across your areas of interest.`
                  : 'Log in to connect with experts across your areas of interest.'
                : expert
                  ? `Get free access to ${getFirstName(expert.name)}'s background, consultation rates, and availability — plus thousands of vetted federal and industry professionals.`
                  : 'Sign up free to access thousands of vetted federal and industry professionals.'}
            </Typography>
          </Box>

          {/* Single-column Form */}
          {view === 'signup' ? (
            <SMBSignupForm
              expert={expert}
              onSuccess={handleSuccess}
              onError={handleError}
              onToggleView={handleToggleView}
              onSignInRequired={handleSignInRequired}
              returnTo={returnTo}
            />
          ) : (
            <SMBLoginForm
              expert={expert}
              onSuccess={handleSuccess}
              onToggleView={handleToggleView}
              handoff={signInHandoff}
              returnTo={returnTo}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SMBSignupModal;
